import React from 'react';
import {StyleSheet, View} from 'react-native';

import {getSkeletonColors, STAGGER} from '../constants';
import {Bone} from '../primitives/Bone';
import {BoneButton} from '../primitives/BoneButton';
import {BoneDivider} from '../primitives/BoneDivider';
import {BoneIcon} from '../primitives/BoneIcon';
import {BoneRow} from '../primitives/BoneRow';
import {BoneText} from '../primitives/BoneText';

/**
 * Skeleton for cart summary / checkout totals card.
 * Mirrors: OrderTotalsLoyaltyEnabled — Card with heading, total rows, divider, CTA
 * Reference: app/screens/cart/components/OrderTotals/OrderTotalsLoyaltyEnabled.tsx
 */
/**
 * `noBorder` mirrors OrderTotals' prop of the same name: on desktop web the totals
 * already sit inside the sidebar card, so the skeleton must not draw a second one.
 */
export const CartSummarySkeleton: React.FC<{noBorder?: boolean}> = React.memo(({noBorder}) => (
  <View>
    {/* Coupon accordion row */}
    <View style={styles.couponRow}>
      <Bone width={150} height={12} borderRadius={2} animationDelay={STAGGER.delay(0)} />
      <BoneIcon size={20} animationDelay={STAGGER.delay(0)} />
    </View>

    {/* Totals card */}
    <View style={noBorder ? styles.cardNoBorder : styles.card}>
      <BoneText variant="h1" width={130} animationDelay={STAGGER.delay(1)} />
      <View style={styles.rows}>
        <BoneRow leftWidth={0.4} rightWidth={0.35} animationDelay={STAGGER.delay(2)} />
        <BoneRow leftWidth={0.5} rightWidth={0.3} animationDelay={STAGGER.delay(3)} />
        <BoneRow leftWidth={0.45} rightWidth={0.2} animationDelay={STAGGER.delay(4)} />
      </View>
      <BoneDivider style={styles.divider} />
      <BoneRow leftWidth={0.55} rightWidth={0.3} height={15} animationDelay={STAGGER.delay(5)} />
      <BoneButton variant="large" animationDelay={STAGGER.delay(6)} />
    </View>
  </View>
));

CartSummarySkeleton.displayName = 'CartSummarySkeleton';

const styles = StyleSheet.create({
  couponRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: getSkeletonColors().cardBackground,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: getSkeletonColors().border,
    padding: 16,
    gap: 12,
  },
  cardNoBorder: {
    padding: 0,
    gap: 12,
  },
  rows: {
    gap: 10,
  },
  divider: {
    marginVertical: 4,
  },
});
